import React, { useEffect, useMemo, useState, useRef } from "react";
import { Controller } from "react-hook-form";
import Box from "@mui/material/Box";
import { useGenerateCampaignFormTikTok } from "../../../../hooks/platforms/tiktok/useGenerateCampaignFormTikTok";
import AnswerContainer from "../../../TypeForm/AnswerContainer";
import AnswerContainerTitleAndField from "../../../TypeForm/AnswerContainerTitleAndField";
import {
  Alert,
  FormControl,
  FormControlLabel,
  FormHelperText,
  LinearProgress,
  Radio,
  RadioGroup,
  Skeleton,
  Typography,
} from "@mui/material";
import AnswerTitle from "../../../TypeForm/AnswerTitle";
import AnswerSubTitle from "../../../TypeForm/AnswerSubtitle";
import ActionsButtons from "../../../TypeForm/ActionsButtons";
import { useTranslationApp } from "../../../../lib/i18next";
import Select from "../../../Form/Select";
import {
  optionsAdsCampaignObjectivesTikTok,
  optionsAdsSalesCampaignTypeTikTok,
  optionsAdsCampaignAppPromotionTypesTikTok,
  optionsAppPromotion,
  optionsTikTokLeadDestinationType,
} from "../../../../utils/options";
import {
  AD_ACCOUNT_TIKTOK_PLACEHOLDER,
  CATALOG_PLACEHOLDER,
  CREATE_ADS_CAMPAIGN_BUDGET_PLACEHOLDER,
  CREATE_CAMPAIGN_TRAFFIC_LINK_PLACEHOLDER,
  END_DATE_PLACEHOLDER,
  ERROR_OCCURRED,
  SELECT_A_OPTION,
  START_DATE_PLACEHOLDER,
  VALUE_EXPRESSED_IN,
  SUGGESTED_CREATIVES_COUNT,
  FIELD_REQUIRED,
  CREATE_ADS_CAMPAIGN_TYPE_BUDGET_DAILY_RADIO_LABEL,
  CREATE_ADS_CAMPAIGN_TYPE_BUDGET_LIFETIME_RADIO_LABEL,
} from "../../../../i18n/keysTranslations";
import {
  ADS_CAMPAIGN_OBJECTIVES_WITH_TRACKING_TIKTOK,
  ADS_CAMPAIGN_OBJECTIVE_SALES_OPTION,
  ADS_CAMPAIGN_APP_PROMOTION_IOS_OPTION,
  ADS_CAMPAIGN_TIKTOK_CAMPAIGN_TYPE_APP_OPTION,
  ADS_CAMPAIGN_TIKTOK_DESTINATION_TYPE_INSTANT_PAGE,
  ADS_TIKTOK_CAMPAIGN_OBJECTIVES_VALID_WITH_IMAGES,
  BORDER_RADIUS,
  DELAY_DEBOUNCE,
  LABELS_TRACKING_CUSTOM_EVENTS,
  TIKTOK,
  TIKTOK_IMAGE_FORMATS,
  ADS_CAMPAIGN_TYPE_BUDGET_DAILY_OPTION,
  ADS_CAMPAIGN_TYPE_BUDGET_LIFETIME_OPTION,
  VIDEO_FORMATS,
} from "../../../../utils/constants";
import SelectBusinessAccount from "../../../Meta/SelectBusinessAccount";
import SelectTrackingIDField from "../../../Form/SelectTrackingIDField";
import SelectCustomEventAds from "../../../Form/SelectCustomEventAds";
import TextField from "../../../Form/TextField";
import DateField from "../../../Form/DateField";
import { addDays, startOfDay } from "date-fns";
import {
  IDENTITY_TIK_TOK_PLACEHOLDER,
  TRACKING_TIKTOK_ID_PLACEHOLDER,
} from "../../../../i18n/keysTranslations";
import SelectTikTokIdentityCustom from "../../../Form/SelectTikTokIdentityCustom";
import _ from "lodash";
import NumberField from "../../../Form/NumberField";
import {
  startGetAudienceProjectionAdsCampaign,
  startGetSuggestedCreativesByBudgetAdsCampaign,
} from "../../../../actions/adsCampaigns";
import { useDispatchApp } from "../../../../lib/redux";
import { useDebounced } from "../../../../hooks/useDebounce";
import SelectAdvertisableAppsTikTok from "../AdGroup/SelectAdvertisableAppsTikTok";
import SelectCatalogTikTok from "../Campaign/SelectCatalogTikTok";
import endOfDay from "date-fns/endOfDay";
import {
  BOX_SHADOW_FIELDS,
  INPUT_COLOR_BORDER,
  GRAY_50,
  GRAY_200,
  GRAY_900,
} from "../../../../utils/colors";
import MediaUrlsAnalysisContainer from "../../../Common/MediaUrlsAnalysisContainer";
import { useHydratePendingGalleryCreatives } from "../../../../hooks/useHydratePendingGalleryCreatives";

const getTikTokGalleryAcceptedFormats = (objective, type) => {
  const isSalesWithApp =
    objective === ADS_CAMPAIGN_OBJECTIVE_SALES_OPTION &&
    type === ADS_CAMPAIGN_TIKTOK_CAMPAIGN_TYPE_APP_OPTION;
  const isCarouselAdsAvailable =
    ADS_TIKTOK_CAMPAIGN_OBJECTIVES_VALID_WITH_IMAGES.includes(objective) &&
    !isSalesWithApp;

  return VIDEO_FORMATS.concat(
    isCarouselAdsAvailable ? TIKTOK_IMAGE_FORMATS : []
  );
};

const GenerateCampaignFormTikTok = ({
  initialData = {},
  campaignID,
  generatedPlatforms = {},
  context = {},
  galleryHydrationRef,
  onCallbackSave = () => {},
  onExitToPlatformSelection,
  verifyBlockAlert,
  resumeValues = null,
}) => {
  const config = useGenerateCampaignFormTikTok({
    ...context,
    campaignID,
    generatedPlatforms,
    onCallbackSave,
    onExitToPlatformSelection,
    verifyBlockAlert,
    resumeValues,
  });
  const currentAnswer = config.currentAnswer;
  const fieldsConfig = config.fields;
  const { setValue, getValues, watch } = config.utils;
  const objective = watch("objective");
  const campaignType = watch("type");
  const acceptedFormats = useMemo(
    () => getTikTokGalleryAcceptedFormats(objective, campaignType),
    [objective, campaignType]
  );
  const steps = config.steps;
  const isLoadingDefaults = config.isLoadingDefaults;

  useEffect(() => {
    if (isLoadingDefaults || _.isEmpty(initialData)) return;

    Object.keys(initialData).forEach((key) => {
      if (key === "destinationURL" && initialData?.[key]) {
        setValue("destinationURL", initialData?.[key]);
      }
      if (
        key === "objective" &&
        initialData[key] &&
        optionsAdsCampaignObjectivesTikTok.find(
          (option) => option.value === initialData?.[key]
        )
      ) {
        setValue("objective", initialData?.[key]);
      }
      if (
        key === "type" &&
        initialData[key] &&
        (optionsAdsSalesCampaignTypeTikTok.find(
          (option) => option.value === initialData?.[key]
        ) ||
          optionsAdsCampaignAppPromotionTypesTikTok.find(
            (option) => option.value === initialData?.[key]
          ))
      ) {
        setValue("type", initialData?.[key]);
      }
    });
    // eslint-disable-next-line
  }, [initialData, isLoadingDefaults]);

  useHydratePendingGalleryCreatives({
    initialData,
    isLoadingDefaults,
    currentAnswer,
    creativeStepName: "mediaUrls",
    fieldName: "mediaUrls",
    platformKey: TIKTOK,
    galleryHydrationRef,
    setValue,
    getValues,
    acceptedFormats,
  });
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        minHeight: 400,
        height: "100%",
      }}
    >
      <Box
        sx={{
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
        }}
      >
        {steps.map((step) => {
          const fieldConfig = fieldsConfig[step];
          if (!fieldConfig.isVisible || currentAnswer !== fieldConfig.name)
            return null;

          return (
            <CommonQuestionContainer
              key={fieldConfig.name}
              formConfig={config}
              fieldConfig={fieldConfig}
              context={context}
            />
          );
        })}
      </Box>
    </Box>
  );
};

const ObjectiveQuestion = ({ fieldConfig, control }) => {
  const { t } = useTranslationApp();
  const { setValue } = fieldConfig.utils;
  return (
    <Select
      options={optionsAdsCampaignObjectivesTikTok}
      name={fieldConfig.name}
      variant="filled"
      fullWidth
      label={t(SELECT_A_OPTION)}
      doTranslate={true}
      control={control}
      actionsChange={() => {
        setValue("identityTikTokID", "");
        setValue("trackingID", "");
        setValue("trackingCustomEventID", "");
      }}
    />
  );
};
const TypeQuestion = ({ fieldConfig, control }) => {
  const { t } = useTranslationApp();
  const { setValue, watch } = fieldConfig.utils;
  const objective = watch("objective");

  const options =
    objective === ADS_CAMPAIGN_OBJECTIVE_SALES_OPTION
      ? optionsAdsSalesCampaignTypeTikTok
      : optionsAdsCampaignAppPromotionTypesTikTok;

  return (
    <Select
      options={options}
      name={fieldConfig.name}
      variant="filled"
      fullWidth
      label={t(SELECT_A_OPTION)}
      doTranslate={true}
      control={control}
      actionsChange={(value) => {
        if (objective === ADS_CAMPAIGN_OBJECTIVE_SALES_OPTION) {
          if (value === ADS_CAMPAIGN_TIKTOK_CAMPAIGN_TYPE_APP_OPTION) {
            setValue("trackingID", "");
          } else {
            setValue("catalogID", "");
          }
        }
        setValue("identityTikTokID", "");
        setValue("trackingCustomEventID", "");
      }}
    />
  );
};

const CatalogIDQuestion = ({ fieldConfig, control }) => {
  const { t } = useTranslationApp();
  const { getValues } = fieldConfig.utils;

  const integrationID = getValues("integrationID");
  const adAccountID = getValues("adAccountID");

  return (
    <SelectCatalogTikTok
      control={control}
      name={fieldConfig.name}
      label={t(CATALOG_PLACEHOLDER)}
      fullWidth
      variant="filled"
      integrationID={integrationID}
      adAccountID={adAccountID}
    />
  );
};

const AppPromotionQuestion = ({ fieldConfig, control }) => {
  const { t } = useTranslationApp();
  const { setValue } = fieldConfig.utils;

  return (
    <Select
      options={optionsAppPromotion}
      name={fieldConfig.name}
      variant="filled"
      fullWidth
      label={t(SELECT_A_OPTION)}
      doTranslate={true}
      control={control}
      actionsChange={(value) => {
        if (value === ADS_CAMPAIGN_APP_PROMOTION_IOS_OPTION) {
          setValue("isIOS14Campaign", true);
        } else {
          setValue("isIOS14Campaign", false);
        }
      }}
    />
  );
};

const IdentitiesQuestion = ({ fieldConfig, control }) => {
  const { t } = useTranslationApp();
  const { watch, setValue } = fieldConfig.utils;

  const integrationID = watch(`integrationID`);
  const adAccountID = watch(`adAccountID`);
  const objective = watch(`objective`);
  const type = watch(`type`);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1,
        width: "100%",
      }}
    >
      <SelectBusinessAccount
        variant="filled"
        label={t(AD_ACCOUNT_TIKTOK_PLACEHOLDER)}
        control={control}
        platform={TIKTOK}
        name={`adAccountID`}
        onChangeAction={(value) => {
          setValue(`integrationID`, value?.integrationID);
          setValue(`identityTikTokID`, "");
          setValue(`trackingID`, "");
          setValue(`trackingCustomEventID`, "");
        }}
        fullWidth={true}
      />
      <SelectTikTokIdentityCustom
        variant="filled"
        objective={objective}
        type={type}
        label={t(IDENTITY_TIK_TOK_PLACEHOLDER)}
        control={control}
        integrationID={integrationID}
        adAccountID={adAccountID}
        name={`identityTikTokID`}
        fullWidth={true}
      />
    </Box>
  );
};

const AppIDQuestion = ({ fieldConfig, control }) => {
  const { t } = useTranslationApp();
  const { getValues } = fieldConfig.utils;

  const integrationID = getValues(`integrationID`);
  const adAccountID = getValues(`adAccountID`);
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 0.5,
        alignItems: "flex-start",
        width: "100%",
      }}
    >
      <SelectAdvertisableAppsTikTok
        placeholder={t(SELECT_A_OPTION)}
        platform={TIKTOK}
        adAccountID={adAccountID}
        integrationID={integrationID}
        name={fieldConfig.name}
        fullWidth
        variant="filled"
        control={control}
      />
    </Box>
  );
};

const TrackingIDQuestion = ({ fieldConfig, control }) => {
  const { t } = useTranslationApp();
  const { getValues, watch, setValue } = fieldConfig.utils;
  const [pixels, setPixels] = useState([]);

  const integrationID = getValues(`integrationID`);
  const adAccountID = getValues(`adAccountID`);
  const objective = watch(`objective`);
  const trackingID = watch(`trackingID`);
  const hasTrackingCustomEventID =
    ADS_CAMPAIGN_OBJECTIVES_WITH_TRACKING_TIKTOK.includes(objective);
  const pixelSelected = _.isEmpty(pixels)
    ? {}
    : pixels.find((p) => p.id === trackingID);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 1,
        width: "100%",
      }}
    >
      <SelectTrackingIDField
        control={control}
        name={fieldConfig.name}
        label={t(TRACKING_TIKTOK_ID_PLACEHOLDER)}
        fullWidth
        variant="filled"
        platform={TIKTOK}
        integrationID={integrationID}
        adAccountID={adAccountID}
        onLoadOptions={(newPixels) => {
          setPixels(newPixels);
        }}
        onChangeAction={() => {
          setValue("trackingCustomEventID", "");
        }}
      />
      {hasTrackingCustomEventID && (
        <SelectCustomEventAds
          name="trackingCustomEventID"
          platform={TIKTOK}
          label={t(LABELS_TRACKING_CUSTOM_EVENTS[TIKTOK])}
          variant="filled"
          integrationID={integrationID}
          adAccountID={adAccountID}
          objective={objective}
          pixelSelected={pixelSelected}
          control={control}
          fullWidth={true}
          rules={{
            required: {
              value: true,
              message: t(FIELD_REQUIRED),
            },
          }}
        />
      )}
    </Box>
  );
};

const LeadDestinationQuestion = ({ fieldConfig, control }) => {
  const { t } = useTranslationApp();
  const { setValue } = fieldConfig.utils;

  return (
    <Select
      options={optionsTikTokLeadDestinationType}
      name="destinationType"
      variant="filled"
      fullWidth
      label={t(SELECT_A_OPTION)}
      doTranslate={true}
      control={control}
      actionsChange={(value) => {
        if (value === ADS_CAMPAIGN_TIKTOK_DESTINATION_TYPE_INSTANT_PAGE) {
          setValue("trackingID", "");
          setValue("trackingCustomEventID", "");
          setValue("destinationURL", "");
          return;
        }
        setValue("instantForm", null);
      }}
    />
  );
};

const DestinationURLQuestion = ({ fieldConfig, control }) => {
  const { t } = useTranslationApp();

  return (
    <TextField
      control={control}
      name={fieldConfig.name}
      fullWidth
      placeholder={t(CREATE_CAMPAIGN_TRAFFIC_LINK_PLACEHOLDER)}
      variant="filled"
    />
  );
};

const TypeBudgetQuestion = ({ fieldConfig, control }) => {
  const { t } = useTranslationApp();

  return (
    <Controller
      control={control}
      name={fieldConfig.name}
      rules={{
        required: { value: true, message: t(FIELD_REQUIRED) },
      }}
      render={({ field, fieldState }) => (
        <FormControl
          component="fieldset"
          variant="standard"
          fullWidth
          error={!!fieldState.error}
        >
          <RadioGroup
            sx={{ gap: 1 }}
            value={field.value ?? ""}
            onChange={(e) => {
              field.onChange(e.target.value);
            }}
          >
            <FormControlLabel
              value={ADS_CAMPAIGN_TYPE_BUDGET_DAILY_OPTION}
              control={<Radio />}
              label={t(CREATE_ADS_CAMPAIGN_TYPE_BUDGET_DAILY_RADIO_LABEL)}
            />
            <FormControlLabel
              value={ADS_CAMPAIGN_TYPE_BUDGET_LIFETIME_OPTION}
              control={<Radio />}
              label={t(CREATE_ADS_CAMPAIGN_TYPE_BUDGET_LIFETIME_RADIO_LABEL)}
            />
          </RadioGroup>
          {fieldState.error?.message ? (
            <FormHelperText>{fieldState.error.message}</FormHelperText>
          ) : null}
        </FormControl>
      )}
    />
  );
};

const BudgetQuestion = ({ fieldConfig, control, currency, context }) => {
  const { t } = useTranslationApp();
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1,
        width: "100%",
      }}
    >
      <TextField
        control={control}
        name={fieldConfig.name}
        placeholder={t(CREATE_ADS_CAMPAIGN_BUDGET_PLACEHOLDER)}
        fullWidth
        variant="filled"
        InputProps={{
          inputComponent: NumberField,
        }}
        helperText={t(VALUE_EXPRESSED_IN, {
          currency,
        })}
      />
      <Projection
        fieldConfig={fieldConfig}
        context={context}
        currency={currency}
      />
    </Box>
  );
};

const DatesQuestion = ({ fieldConfig, control }) => {
  const { t } = useTranslationApp();
  const { watch, setValue } = fieldConfig.utils;
  const startDate = watch("startDate");
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1,
        width: "100%",
      }}
    >
      <DateField
        sx={{
          backgroundColor: "fields.background",
          boxShadow: BOX_SHADOW_FIELDS,
          border: `1px solid ${INPUT_COLOR_BORDER}`,
          borderRadius: BORDER_RADIUS,
        }}
        variant="filled"
        control={control}
        name="startDate"
        label={t(START_DATE_PLACEHOLDER)}
        actionsChange={() => {
          setValue("endDate", null);
        }}
        minDate={startOfDay(new Date())}
        showStartNowButton={true}
      />
      <DateField
        sx={{
          backgroundColor: "fields.background",
          boxShadow: BOX_SHADOW_FIELDS,
          border: `1px solid ${INPUT_COLOR_BORDER}`,
          borderRadius: BORDER_RADIUS,
        }}
        variant="filled"
        control={control}
        name="endDate"
        label={t(END_DATE_PLACEHOLDER)}
        defaultCalendarMonth={addDays(startDate || new Date(), 1)}
        actionsChange={(value) => {
          setValue(`endDate`, value ? endOfDay(value) : null);
        }}
        minDate={startDate ? addDays(startDate, 1) : addDays(new Date(), 1)}
      />
    </Box>
  );
};

const MediaUrlsQuestion = ({ fieldConfig, currency }) => {
  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();
  const [suggestedCreativesCount, setSuggestedCreativesCount] = useState(null);
  const hasFetchedSuggestedCreativesRef = useRef(false);
  const { setValue, getValues, watch } = fieldConfig.utils;
  const mediaUrls = watch("mediaUrls") || [];
  const objective = watch("objective");
  const context = watch("context");

  useEffect(() => {
    if (hasFetchedSuggestedCreativesRef.current) return;

    const budget = getValues("budget");
    const typeBudget = getValues("typeBudget");
    const startDate = getValues("startDate");
    const endDate = getValues("endDate");

    const shouldSkip =
      !budget || !typeBudget || !currency || fieldConfig.name !== "mediaUrls";

    if (shouldSkip) return;

    hasFetchedSuggestedCreativesRef.current = true;
    setSuggestedCreativesCount(null);

    const fetchSuggestedCreatives = async () => {
      const response = await dispatch(
        startGetSuggestedCreativesByBudgetAdsCampaign({
          platform: TIKTOK,
          budget,
          typeBudget,
          currency,
          startDate,
          endDate,
        })
      );

      const count =
        response.ok && response.data?.suggestedCreativesByBudget
          ? response.data.suggestedCreativesByBudget
          : null;

      setSuggestedCreativesCount(count);
    };

    fetchSuggestedCreatives();
  }, [currency, dispatch, fieldConfig.name, getValues]);

  const mediaUrlsAnalysisRef = useRef(null);

  const onCallbackCampaignMediaImport = ({ entries }) => {
    if (!entries?.length) return;
    const current = getValues("mediaUrls") || [];
    setValue("mediaUrls", [...current, ...entries]);
    requestAnimationFrame(() => {
      mediaUrlsAnalysisRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    });
  };

  // Use URL as identifier to avoid sync issues with async operations
  const onChangeItem = ({ url, data = {} }) => {
    if (!url) return; // Safety check
    const currentMediaUrls = getValues("mediaUrls") || [];
    // Find item by URL (for individual items) or by carousel identifier
    const index = currentMediaUrls.findIndex((item) => {
      if (item?.isCarousel && item?.carouselImages) {
        // For carousels, check if any carouselImage matches the URL
        return item.carouselImages.some((img) => {
          const imgUrl = typeof img === "string" ? img : img?.url;
          return imgUrl === url;
        });
      }
      return item?.url === url;
    });
    if (index === -1) return; // Element no longer exists, ignore update
    const currentData = currentMediaUrls[index];
    if (!currentData) return; // Safety check
    const newData = {
      ...currentData,
      ...data,
    };
    setValue(`mediaUrls.[${index}]`, newData);
  };
  const onRemoveItem = (index) => {
    setValue(
      "mediaUrls",
      mediaUrls.filter((_, i) => i !== index)
    );
  };

  const shouldShowSuggestedCreativesHint =
    suggestedCreativesCount !== null && mediaUrls.length === 0;

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 2,
        position: "relative",
      }}
    >
      <MediaUrlsAnalysisContainer
        containerRef={mediaUrlsAnalysisRef}
        mediaUrls={mediaUrls}
        onRemoveItem={onRemoveItem}
        onChangeItem={onChangeItem}
        objective={objective}
        campaignContext={context}
        platform={TIKTOK}
        suggestedCreativesCount={suggestedCreativesCount}
        onCallbackCampaignMediaImport={onCallbackCampaignMediaImport}
      />
      {shouldShowSuggestedCreativesHint && (
        <Typography
          variant="body2"
          sx={{
            mt: 1,
            textAlign: "center",
            color: "text.secondary",
            position: "absolute",
            bottom: -120,
            left: "50%",
            transform: "translateX(-50%)",
          }}
        >
          {t(SUGGESTED_CREATIVES_COUNT, {
            count: suggestedCreativesCount,
          })}
        </Typography>
      )}
    </Box>
  );
};
const Projection = ({ fieldConfig, context, currency }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [text, setText] = useState("");
  const abortControllerRef = useRef(null);
  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();

  const { getValues, watch } = fieldConfig.utils;
  const budget = watch("budget");
  const locationsContext = context.locations || { locations: [] };
  const locations = locationsContext?.locations?.map(
    (location) => location.name
  );
  const objectContext = {
    locations,
    typeBudget: getValues("typeBudget"),
    budget: getValues("budget"),
    startDate: getValues("startDate"),
    endDate: getValues("endDate"),
    platform: TIKTOK,
    currency,
  };

  const onGetInitialData = useDebounced(async () => {
    setText("");
    if (!objectContext.budget || budget < 1) return;
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;
    setIsLoading(true);
    const response = await dispatch(
      startGetAudienceProjectionAdsCampaign({
        ...objectContext,
        signal: controller.signal,
      })
    );
    if (response?.aborted) return;
    if (response.ok) {
      setText(response.data.text);
    } else {
      setText(t(ERROR_OCCURRED));
    }
    setIsLoading(false);
  }, [DELAY_DEBOUNCE * 1.5]);

  useEffect(() => {
    onGetInitialData();
    return () => abortControllerRef.current?.abort();
    // eslint-disable-next-line
  }, [budget]);

  if (!text && !isLoading) return null;

  return (
    <Box
      sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 2 }}
    >
      {isLoading ? (
        <>
          <Skeleton variant="rectangular" width="100%" height={60} />
        </>
      ) : (
        <Typography variant="body2">{text}</Typography>
      )}
    </Box>
  );
};

const MediaUrlsSubtitle = ({ fieldConfig, t }) => {
  const subtitleText = fieldConfig.subtitle
    ? t(fieldConfig.subtitle, fieldConfig.subtitleExtrapolation)
    : "";

  if (!subtitleText) return null;

  return (
    <Box
      sx={{
        backgroundColor: GRAY_50,
        border: `1px solid ${GRAY_200}`,
        borderRadius: BORDER_RADIUS,
        width: "100%",
        p: 2,
        mb: 2,
      }}
    >
      <Typography
        variant="body2"
        sx={{
          fontWeight: 500,
          color: GRAY_900,
          fontSize: "16px",
        }}
      >
        {subtitleText}
      </Typography>
    </Box>
  );
};

const COMPONENTS = {
  objective: <ObjectiveQuestion />,
  type: <TypeQuestion />,
  appPromotion: <AppPromotionQuestion />,
  identities: <IdentitiesQuestion />,
  appID: <AppIDQuestion />,
  leadDestination: <LeadDestinationQuestion />,
  trackingID: <TrackingIDQuestion />,
  catalogID: <CatalogIDQuestion />,
  destinationURL: <DestinationURLQuestion />,
  typeBudget: <TypeBudgetQuestion />,
  budget: <BudgetQuestion />,
  dates: <DatesQuestion />,
  mediaUrls: <MediaUrlsQuestion />,
};

const CommonQuestionContainer = ({ formConfig, fieldConfig, context }) => {
  const { t } = useTranslationApp();

  const progress = formConfig.progress;
  const { control, setValue } = fieldConfig.utils;
  const error = formConfig.error;

  const COMPONENT = COMPONENTS[fieldConfig.name];

  if (!COMPONENT) return null;

  return (
    <>
      <LinearProgress
        sx={{
          width: "100%",
          mb: 3,
          maxWidth: 600,
          backgroundColor: "rgba(0,0,0,0.2)",
          "& .MuiLinearProgress-bar": { backgroundColor: "black" },
        }}
        variant="determinate"
        value={progress}
      />
      <AnswerContainer sx={{ display: "flex", height: "100%" }}>
        <AnswerContainerTitleAndField>
          {fieldConfig.title && (
            <AnswerTitle sx={{ mb: 1 }}>
              {`${t(fieldConfig.title, fieldConfig.titleExtrapolation)}`}
            </AnswerTitle>
          )}
          {fieldConfig.name === "mediaUrls" && fieldConfig.subtitle ? (
            <MediaUrlsSubtitle fieldConfig={fieldConfig} t={t} />
          ) : (
            fieldConfig.subtitle && (
              <AnswerSubTitle>
                {t(fieldConfig.subtitle, fieldConfig.subtitleExtrapolation)}
              </AnswerSubTitle>
            )
          )}
          {error && (
            <Alert
              severity="error"
              sx={{
                mb: 1,
                backgroundColor: "fields.background",
                color: "background.paperContrastText",
                border: `1px solid ${INPUT_COLOR_BORDER}`,
                borderRadius: BORDER_RADIUS,
                boxShadow: BOX_SHADOW_FIELDS,
              }}
            >
              {error}
            </Alert>
          )}
          {React.cloneElement(COMPONENT, {
            fieldConfig,
            control,
            setValue,
            error,
            currency: formConfig.currency,
            context,
          })}
        </AnswerContainerTitleAndField>
        {fieldConfig.showActionsButtons && (
          <>
            <ActionsButtons
              onPreviousStep={fieldConfig.onPreviousStep}
              showPreviousButton={fieldConfig.showPreviousButton}
              disabledPreviousButton={fieldConfig.disabledPreviousButton}
              onNextStep={fieldConfig.onNextStep}
              showNextButton={fieldConfig.showNextButton}
              disabledNextButton={fieldConfig.disabledNextButton}
              nextButtonText={fieldConfig.nextButtonText}
              saveButtonText={fieldConfig.saveButtonText}
              disabledSaveButton={fieldConfig.disabledFinishButton}
              showSaveButton={fieldConfig.isFinishQuestion}
              onFinish={fieldConfig.onFinish}
              showLoadingComponent={fieldConfig.showLoadingComponent}
              customComponentPreviousNext={
                fieldConfig.customComponentPreviousNext
              }
              loading={formConfig.isCreating}
            />
          </>
        )}
      </AnswerContainer>
    </>
  );
};

export default GenerateCampaignFormTikTok;
